'use client';

import { useState, type FormEvent } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { AuthLayout } from '@/components/auth-layout';
import { useAuth } from '@/lib/auth-context';

export default function SignUpPage() {
  const router = useRouter();
  const { signUp } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!name || !email || !password) {
      setError('Completa todos los campos.');
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (!accept) {
      setError('Acepta los Términos y la Política de privacidad para continuar.');
      return;
    }
    setLoading(true);
    try {
      await signUp(name, email, password);
      router.push('/mi-estanteria');
    } catch (err) {
      setError(
        err instanceof Error && err.message === 'email_taken'
          ? 'Ese correo ya tiene una cuenta. Inicia sesión.'
          : 'No pudimos crear tu cuenta. Inténtalo de nuevo en un momento.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      eyebrow="Crear cuenta"
      title="Únete a la Biblioteca FCE"
      intro="Tu cuenta es gratuita y abre acceso a todo el catálogo digital del Fondo."
      footer={
        <>
          ¿Ya tienes cuenta?{' '}
          <Link href="/iniciar-sesion" className="under" style={{ color: 'var(--ink)', fontWeight: 500 }}>
            Inicia sesión
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <label>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Nombre completo</div>
          <input
            className="input"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Tu nombre y apellidos"
            required
          />
        </label>
        <label>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Correo electrónico</div>
          <input
            className="input"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        <label>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Contraseña</div>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            required
          />
        </label>
        <label>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Confirmar contraseña</div>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            minLength={8}
            required
          />
        </label>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: 'var(--ink-soft)' }}>
          <input
            type="checkbox"
            className="check"
            checked={accept}
            onChange={(e) => setAccept(e.target.checked)}
            style={{ marginTop: 2 }}
          />
          <span>
            Acepto los{' '}
            <Link href="/terminos" className="under" style={{ color: 'var(--ink)' }}>Términos</Link>
            {' '}y la{' '}
            <Link href="/politica-de-privacidad" className="under" style={{ color: 'var(--ink)' }}>
              Política de privacidad
            </Link>
            .
          </span>
        </label>

        {error && <div style={{ color: 'var(--accent)', fontSize: 13 }}>{error}</div>}

        <button type="submit" className="btn btn-primary" disabled={loading} style={{ justifyContent: 'center', marginTop: 8 }}>
          {loading ? 'Creando cuenta…' : 'Crear cuenta'}
        </button>
      </form>
    </AuthLayout>
  );
}
